import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Edit, Trash2, Upload, Download, Save, X, Search, Landmark } from "lucide-react";

const blank = { bank_name: "", bank_address: "", account_number: "", account_type: "", linked_company_id: "", linked_company_name: "", opening_balance: "", opening_balance_date: "" };
const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";

export default function BankMaster() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "gm";
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reload = async () => {
    const { data } = await api.get("/banks");
    setRows(data || []);
  };
  useEffect(() => {
    reload();
    api.get("/companies-book").then(({ data }) => setCompanies(data || []));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const k = q.toLowerCase();
    return rows.filter((r) =>
      [r.bank_name, r.bank_address, r.account_number, r.account_type]
        .some((v) => String(v || "").toLowerCase().includes(k))
    );
  }, [rows, q]);

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({
      bank_name: r.bank_name || "",
      bank_address: r.bank_address || "",
      account_number: r.account_number || "",
      account_type: r.account_type || "",
      linked_company_id: r.linked_company_id || "",
      linked_company_name: r.linked_company_name || "",
      opening_balance: r.opening_balance ?? "",
      opening_balance_date: r.opening_balance_date || "",
    });
  };

  const startNew = () => { setEditing("new"); setForm(blank); };
  const cancel = () => { setEditing(null); setForm(blank); };

  const save = async () => {
    if (!form.bank_name.trim()) return toast.error("Bank name is required");
    if (!form.account_number.trim()) return toast.error("Account number is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: form.opening_balance === "" ? 0 : Number(form.opening_balance) || 0,
      };
      if (editing === "new") {
        await api.post("/banks", payload);
        toast.success("Bank added");
      } else {
        await api.put(`/banks/${editing}`, payload);
        toast.success("Bank updated");
      }
      cancel();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete bank ${r.bank_name} · ${r.account_number}?`)) return;
    try {
      await api.delete(`/banks/${r.id}`);
      toast.success("Bank deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const res = await fetch(`${API}/banks/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bank_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/banks/import", fd);
      toast.success(`Inserted ${data.inserted}, skipped ${data.skipped}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="bank-master">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Master · Bank Accounts</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Landmark size={22} /> Bank Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">Bank accounts used in supplier payment vouchers.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadTemplate} className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
              <Download size={14} /> Template
            </button>
            <label className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 cursor-pointer">
              <Upload size={14} /> Import
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onImport} className="hidden" />
            </label>
            <button onClick={startNew} data-testid="bank-add-btn"
              className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
              <Plus size={14} /> Add Bank
            </button>
          </div>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bank / account…"
          className={`${inputCls} pl-9`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Bank Name</th>
              <th className="px-3 py-2 text-left">Linked Company</th>
              <th className="px-3 py-2 text-left">Address</th>
              <th className="px-3 py-2 text-left">Account No</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-right">Opening Bal</th>
              {canManage && <th className="px-3 py-2 text-center w-20">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={canManage ? 7 : 6} className="text-center py-6 text-slate-400 italic">No banks found</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold">{r.bank_name}</td>
                <td className="px-3 py-2 text-slate-700">{r.linked_company_name || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-3 py-2 text-slate-600">{r.bank_address}</td>
                <td className="px-3 py-2 font-mono">{r.account_number}</td>
                <td className="px-3 py-2">{r.account_type}</td>
                <td className="px-3 py-2 text-right font-mono">{r.opening_balance ? `₹ ${Number(r.opening_balance).toLocaleString("en-IN")}` : <span className="text-slate-400">—</span>}</td>
                {canManage && (
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => startEdit(r)} className="p-1.5 text-[#1D2951] hover:bg-slate-100 rounded">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => del(r)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
                      <Trash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={cancel}>
          <div className="bg-white rounded-lg max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{editing === "new" ? "Add Bank" : "Edit Bank"}</h2>
              <button onClick={cancel} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Bank Name <span className="text-rose-500">*</span></label>
                <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  data-testid="bank-name" className={inputCls} autoFocus />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Account Type</label>
                <input value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                  data-testid="bank-account-type" className={inputCls} placeholder="Current / Savings / CC" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Bank Address</label>
                <input value={form.bank_address} onChange={(e) => setForm({ ...form, bank_address: e.target.value })}
                  data-testid="bank-address" className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Account Number <span className="text-rose-500">*</span></label>
                <input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  data-testid="bank-account-number" className={`${inputCls} font-mono`} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Linked Company (for Bank Reco)</label>
                <select value={form.linked_company_id}
                  onChange={(e) => {
                    const c = companies.find((x) => x.id === e.target.value);
                    setForm({ ...form, linked_company_id: e.target.value, linked_company_name: c?.name || "" });
                  }}
                  data-testid="bank-linked-company" className={inputCls}>
                  <option value="">— none —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Opening Balance (₹)</label>
                <input type="number" step="any" value={form.opening_balance}
                  onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
                  data-testid="bank-opening-balance" className={`${inputCls} font-mono`} placeholder="0" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Opening Date</label>
                <input type="date" value={form.opening_balance_date}
                  onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })}
                  data-testid="bank-opening-date" className={`${inputCls} font-mono`} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={cancel} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="bank-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Save size={14} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
